import * as React from "react";
import { createPortal } from "react-dom";
import { cn } from "../lib/utils";

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  width?: number;
  closeOnBackdrop?: boolean;
  children?: React.ReactNode;
}

export function Modal({
  open,
  onClose,
  title,
  description,
  width = 420,
  closeOnBackdrop = true,
  children,
}: ModalProps) {
  React.useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/35"
      onClick={() => closeOnBackdrop && onClose()}
    >
      <div
        className={cn(
          "bg-surface border border-border rounded-lg p-7",
          "max-w-[calc(100vw-32px)]",
        )}
        style={{ width }}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        {title && (
          <p className="text-[15px] font-semibold tracking-tight mb-1.5">
            {title}
          </p>
        )}
        {description && (
          <p className="text-xs text-ink-muted mb-5 leading-relaxed">
            {description}
          </p>
        )}
        {children}
      </div>
    </div>,
    document.body,
  );
}
